import {
  ref, inject, computed, onMounted,
} from 'vue';
import useAddToCart from './useAddToCart';
import useDeleteFromCart from './useDeleteFromCart';
import useUpdateCartQuantity from './useUpdateCartQuantity';

export default function useCartSummaryItem(props, emit) {
  const { addToCartTag } = useAddToCart();
  const {
    deleteFromCart, removeFromCartTag, isLoading: isDeleting,
  } = useDeleteFromCart();
  const { updateCartQuantity, isLoading: isUpdating } = useUpdateCartQuantity();
  const trans = inject('translate');
  const currentActionLoading = ref(null);
  const isStockLimited = ref(false);

  const checkStock = () => {
    if (props.item.product.isSerenityPack) {
      return;
    }
    if (props.item.availableQuantity === 0) {
      emit('item-out-of-stock', props.item);
      deleteFromCart(props.item.id);
    } else if (props.item.availableQuantity < props.item.quantity) {
      isStockLimited.value = true;
      updateCartQuantity(props.item.id, props.item.availableQuantity);
    } else {
      isStockLimited.value = false;
    }
  };

  onMounted(() => {
    checkStock();
  });

  const displayPrice = computed(() => {
    if (props.item.product.isVirtual) {
      return null;
    }
    const { price } = props.item.product;
    if (props.isPro) {
      return price.discountedBasePrice
        ? price.displayableDiscountAmountIncludingSpecificTax
        : price.displayablePriceIncludingSpecificTax;
    }
    return price.discountedBasePrice
      ? price.displayableDiscountAmountIncludingAllTaxes
      : price.displayablePriceIncludingAllTaxes;
  });

  const originalPrice = computed(() => {
    if (props.item.product.isVirtual) {
      return null;
    }
    const { price } = props.item.product;
    return props.isPro
      ? price.displayablePriceIncludingSpecificTax
      : price.displayablePriceIncludingAllTaxes;
  });

  const amountItemsPrice = computed(() => {
    const { price } = props.item.product;
    let currentItemPrice;
    if (props.item.product.isVirtual) {
      currentItemPrice = props.item.orderItem?.unit_price;
    } else if (props.isPro) {
      currentItemPrice = price.discountedBasePrice
        ? price.discountedPriceIncludingSpecificTax
        : price.priceIncludingSpecificTax;
    } else {
      currentItemPrice = price.discountedBasePrice
        ? price.discountedPriceIncludingAllTaxes
        : price.priceIncludingAllTaxes;
    }

    const roundedItemPrice = Math.round(currentItemPrice * 100) / 100;

    return (roundedItemPrice * props.item.quantity).toFixed(2).replace('.', ',');
  });

  const hasDiscount = computed(() => !!props.item.product.price?.discountedBasePrice);

  function increaseQuantity(item) {
    if (isUpdating.value || item.product.isVirtual) return;
    if (item.quantity < item.availableQuantity) {
      currentActionLoading.value = 'increase';
      updateCartQuantity(item.id, item.quantity + 1, () => {
        currentActionLoading.value = null;
      });
      addToCartTag(item.product, 1, item.product.price, null);
    }
  }

  function decreaseQuantity(item) {
    if (isUpdating.value || item.product.isVirtual) return;
    if (item.quantity > 1) {
      currentActionLoading.value = 'decrease';
      updateCartQuantity(item.id, item.quantity - 1, () => {
        currentActionLoading.value = null;
      });
      removeFromCartTag(item, 1);
    }
  }

  function deleteItem(item) {
    if (isDeleting.value) return;
    currentActionLoading.value = 'delete';
    removeFromCartTag(item);
    deleteFromCart(item.id);
  }

  return {
    trans,
    currentActionLoading,
    isStockLimited,
    displayPrice,
    originalPrice,
    amountItemsPrice,
    hasDiscount,
    deleteItem,
    increaseQuantity,
    decreaseQuantity,
    checkStock,
  };
}
